import { isoCountryCodes } from '@tinkr/shared/countries';
import { useId, useMemo, useRef, useState } from 'react';
import type { SupportedLocale, Translate } from '../i18n';
import './ProviderSignupCountryPicker.css';

/** ISO countries are presentation data; only the API decides which can onboard. */
export function ProviderSignupCountryPicker({ value, onChange, countries, locale, t, disabled = false }: {
  value: string;
  onChange: (country: string) => void;
  countries: readonly string[] | null;
  locale: SupportedLocale;
  t: Translate;
  disabled?: boolean;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const options = useMemo(() => {
    let names: Intl.DisplayNames | undefined;
    try { names = new Intl.DisplayNames([locale], { type: 'region' }); } catch { /* ISO labels remain usable. */ }
    const enabled = new Set(countries ?? []);
    return isoCountryCodes.map(code => ({ code, label: `${names?.of(code) ?? code} (${code})`, enabled: enabled.has(code) }))
      .sort((a, b) => Number(b.enabled) - Number(a.enabled) || a.label.localeCompare(b.label, locale));
  }, [countries, locale]);
  const selectedLabel = options.find(option => option.code === value)?.label ?? '';
  const results = options.filter(option => option.label.toLocaleLowerCase(locale).includes(query.trim().toLocaleLowerCase(locale)));
  const selectable = results.filter(option => option.enabled);

  function choose(code: string) {
    if (!countries?.includes(code) || disabled) return;
    onChange(code);
    setOpen(false);
    setQuery('');
    setActiveCode(null);
  }

  return <div className="provider-country-picker" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) { setOpen(false); setQuery(''); setActiveCode(null); }
  }}>
    <div className="provider-country-control">
    <label className="warm-field" htmlFor={id}>
      <span className="warm-field-label">{t('auth.accountCountry')}</span>
      <input id={id} role="combobox" aria-autocomplete="list" aria-expanded={open}
        aria-controls={`${id}-options`} aria-activedescendant={open && activeCode ? `${id}-${activeCode}` : undefined}
        aria-describedby={`${id}-help`} autoComplete="off" required disabled={disabled}
        placeholder={open ? t('auth.countrySearch') : t('auth.accountCountryPlaceholder')}
        value={open ? query : selectedLabel}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onClick={() => setOpen(true)}
        onChange={event => { setQuery(event.target.value); setActiveCode(null); onChange(''); setOpen(true); }}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); setOpen(false); setQuery(''); return; }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault(); setOpen(true);
            const index = selectable.findIndex(option => option.code === activeCode);
            const next = selectable[index < 0 ? (event.key === 'ArrowDown' ? 0 : selectable.length - 1)
              : (index + (event.key === 'ArrowDown' ? 1 : -1) + selectable.length) % selectable.length];
            setActiveCode(next?.code ?? null);
            if (next) listRef.current?.querySelector<HTMLElement>(`[data-country="${next.code}"]`)?.scrollIntoView?.({ block: 'nearest' });
          } else if (event.key === 'Enter' && open) {
            event.preventDefault();
            if (activeCode) choose(activeCode);
            else if (selectable.length === 1) choose(selectable[0].code);
          }
        }} />
    </label>
    {open && <ul ref={listRef} id={`${id}-options`} role="listbox" aria-label={t('auth.accountCountry')}
      className="provider-country-options" onMouseDown={event => event.preventDefault()}>
      {results.map(option => <li key={option.code} id={`${id}-${option.code}`} data-country={option.code}
        role="option" aria-selected={value === option.code} aria-disabled={!option.enabled}
        data-active={activeCode === option.code} onMouseEnter={() => setActiveCode(option.enabled ? option.code : null)}
        onClick={() => choose(option.code)}>
        <span>{option.label}</span>
        {!option.enabled && <small>{t('auth.countryNotOpen')}</small>}
      </li>)}
      {results.length === 0 && <li role="presentation">{t('auth.countryNoMatches')}</li>}
    </ul>}
    </div>
    <span id={`${id}-help`} className="warm-field-help">{t('auth.accountCountryHelp')}</span>
    <span className="warm-field-help">{t('auth.countryAvailabilityHelp')}</span>
  </div>;
}
